import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Alert, Spinner } from '@/components/ui';
import { useAuth } from '@/context/AuthContext';
import type { Capabilities } from '@/services/types';

/** Blocks a route until a session is confirmed, remembering where the user was heading. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner label="Checking your session" />
      </div>
    );
  }

  if (status === 'anonymous') {
    return <Navigate to="/admin/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return <>{children}</>;
}

/**
 * Route-level capability check.
 *
 * The sidebar already hides links a role cannot use, but hiding a link is not access control — this
 * is what actually stops a typed URL. The server enforces the same rule again; this only improves
 * the message the user sees.
 */
export function RequireCapability({ capability, children }: { capability: keyof Capabilities; children: ReactNode }) {
  const { can, user } = useAuth();

  if (!can(capability)) {
    return (
      <div className="mx-auto max-w-lg py-16">
        <Alert tone="warning" title="You do not have access to this area">
          Your role ({user?.primary_role.replace('_', ' ')}) does not include this permission. Ask a Super Admin if you
          need it.
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
}

/** Hides a control the current role cannot use. Presentation only — never the sole protection. */
export function IfCan({ capability, children }: { capability: keyof Capabilities; children: ReactNode }) {
  const { can } = useAuth();
  return can(capability) ? <>{children}</> : null;
}
